// ── BUSHELS BY COUNTY — attributing production to the ground it came off ─────
//
// Kevin, 2026-09-28: *"The only way we will be able to attribute bushels to a county is
// from John Deere data if that is connected, or by us manually entering bushels or scale
// tickets by county."*
//
// ⚠ A CLAIM SETTLES PER UNIT, AND A SCALE TICKET CANNOT SAY WHERE THE BUSHELS CAME FROM.
// Measured over all 1,907 tickets on file: the columns are `buyer`, `location` and
// `contract_ref` — the ELEVATOR's location, not the farm's county. A grower hauling two
// counties' corn to one elevator produces tickets that are indistinguishable. There is no
// derivation hiding in the data, which is why this is an INPUT and not a computation.
//
// This module does the arithmetic around that input and nothing else. It states three
// things and refuses to smooth over any of them:
//
//   1. what has been attributed, per county
//   2. what is left over — production on file that no county claims
//   3. what is OVER-attributed — more bushels placed than the farm has, which is an entry
//      error and must never be silently accepted
//
// ⚠ ABSENT IS NOT ZERO, AND HERE IT IS WORTH MORE THAN ANYWHERE ELSE IN THIS REPO. A
// county with no entry is UNSETTLED. Read as zero it is a total loss on that county —
// the largest indemnity the policy can pay — so a blank must never fall through to 0.

const num = (v) => (v == null || v === "" || !isFinite(Number(v)) ? null : Number(v));
// Bushels are compared against a guarantee to the bushel and multiplied by a price to the
// cent, so the sums run in integer hundredths to keep float drift out of a frozen figure.
const hundredths = (v) => Math.round((num(v) || 0) * 100);
const fromH = (h) => h / 100;

// `appraisal` is a third provenance, added 2026-10-06: an appraised GRAIN figure is
// production-to-count for chopped acres, placed in a county through the insured field
// map's FSN (never through the appraisal row's own county, which is stamped from the
// sheet header). Like `jd` it is OFFERED and never filled in, so a stated figure still
// records which of the three a human accepted.
export const SOURCE = Object.freeze({ MANUAL: "manual", JD: "jd", APPRAISAL: "appraisal" });

// A county is SETTLED only when somebody has stated a figure for it. Everything else —
// missing row, null bushels, blank string — is "not stated".
export function countyIsStated(entry) {
  return !!entry && num(entry.bushels) != null;
}

// ── THE RECONCILIATION ───────────────────────────────────────────────────────
//
// `counties`  the counties this farm insures the crop in — from crop_coverage, so a
//             county with coverage and no production still appears and reads UNSETTLED
//             rather than vanishing.
// `entries`   rows from claim_county_production, keyed by county.
// `totalProduction`  the farm-wide figure the claim tool already computes (RMA-net
//             tickets + stored + seed + fed). NULL when nothing is on file, which is
//             different from zero and is reported as such.
// `unit`      what this crop's production is measured in — "bu", "cwt", "lbs", "ton".
//             ⚠ DEFAULTS TO "bu" AND THAT DEFAULT IS A REAL RISK: check-shared-rules
//             counts POSITIONAL arguments, so a caller that forgets this KEY gets
//             bushels silently — the same shape as the dropped `bfr` key. The default
//             is correct for corn, soybeans and wheat and wrong for potatoes (cwt),
//             dry beans (lbs) and grapes (ton), so the one call site is asserted.
export function reconcileCountyProduction({ counties = [], entries = [], totalProduction = null, unit = "bu" } = {}) {
  const byCounty = new Map();
  for (const e of entries || []) {
    const c = String(e.county || "").trim().toUpperCase();
    if (c) byCounty.set(c, e);
  }

  const rows = (counties || []).map((c) => {
    const county = String(c || "").trim().toUpperCase();
    const e = byCounty.get(county) || null;
    const stated = countyIsStated(e);
    return {
      county,
      stated,
      bushels: stated ? num(e.bushels) : null,
      uom: (e && e.uom) || "bu",
      source: stated ? (e.source || SOURCE.MANUAL) : null,
      note: (e && e.note) || null,
      // Said in words on the row, because "—" in a bushels column is exactly the thing a
      // reader fills in with zero.
      why: stated ? null : "no production stated for this county — it cannot be settled",
    };
  });

  // ⚠ AN ENTRY FOR A COUNTY THE FARM DOES NOT INSURE IS SHOWN, NOT DROPPED. It is either
  // a typo or a county we do not know they farm, and both are worth seeing. Dropping it
  // would also make the attributed total disagree with the rows above it.
  const orphans = [];
  for (const [county, e] of byCounty) {
    if (rows.some((r) => r.county === county)) continue;
    if (!countyIsStated(e)) continue;
    orphans.push({ county, bushels: num(e.bushels), uom: e.uom || "bu", source: e.source || SOURCE.MANUAL,
      why: "this farm has no coverage for this crop in that county" });
  }

  const statedRows = rows.filter((r) => r.stated);
  const attributedH = statedRows.reduce((s, r) => s + hundredths(r.bushels), 0)
                    + orphans.reduce((s, r) => s + hundredths(r.bushels), 0);
  const attributed = fromH(attributedH);
  const total = num(totalProduction);

  // ⚠ THREE STATES, AND COLLAPSING ANY TWO LOSES THE ONE THAT MATTERS.
  //   - no total on file        -> we cannot say whether the entries are complete
  //   - attributed < total      -> a REMAINDER nobody has placed
  //   - attributed > total      -> an ENTRY ERROR; you cannot have more by county than
  //                                in total, and accepting it would overstate a loss
  const remainder = total == null ? null : fromH(hundredths(total) - attributedH);
  const over = remainder != null && remainder < -0.005;

  return {
    rows, orphans,
    attributed,
    total,
    // Positive = production on file that no county claims. Null = no farm total to
    // compare against, which is not the same as "all placed".
    remainder: remainder == null ? null : (over ? remainder : Math.max(0, remainder)),
    over,
    countiesStated: statedRows.length,
    countiesTotal: rows.length,
    complete: rows.length > 0 && statedRows.length === rows.length
              && total != null && remainder != null && Math.abs(remainder) <= 0.005,
    // One sentence a screen can print without re-deriving anything.
    status: buildStatus({ rows, statedRows, total, remainder, over, orphans, unit }),
  };
}

function buildStatus({ rows, statedRows, total, remainder, over, orphans, unit = "bu" }) {
  if (!rows.length) return { kind: "no_coverage", text: "no coverage for this crop and year" };
  if (over) {
    return { kind: "over", text: `${fmt(Math.abs(remainder))} more ${unit} are placed by county than the `
      + `farm has on file — check the entries before this is used in an estimate` };
  }
  if (!statedRows.length) {
    return { kind: "none", text: `no production stated for any of the ${rows.length} `
      + `count${rows.length === 1 ? "y" : "ies"} — enter production by county to settle this claim` };
  }
  if (statedRows.length < rows.length) {
    const missing = rows.filter((r) => !r.stated).map((r) => r.county);
    return { kind: "partial", text: `${statedRows.length} of ${rows.length} counties stated — `
      + `${missing.join(", ")} ${missing.length === 1 ? "is" : "are"} not settled` };
  }
  if (total == null) {
    return { kind: "no_total", text: "every county is stated, but there is no farm-wide "
      + "production on file to check the total against" };
  }
  if (remainder != null && remainder > 0.005) {
    return { kind: "remainder", text: `${fmt(remainder)} ${unit} on file are not attributed `
      + `to any county` };
  }
  return { kind: "complete", text: `all ${rows.length} counties stated and the total ties` };
}

const fmt = (v) => Math.round(Math.abs(num(v) || 0)).toLocaleString();

// ── WHAT JOHN DEERE WOULD SAY ────────────────────────────────────────────────
//
// ⚠ A SUGGESTION, NEVER A VALUE. Kevin named John Deere as one of the two routes, and it
// is a good one where a farm is connected — but it is MACHINE production, not the scale
// tickets a claim settles on, and the two differ for ordinary reasons (moisture basis,
// a monitor never calibrated, grain left in the field). So this returns a figure to OFFER
// beside the input; nothing here writes, and the UI must require a click. Accepting it
// stamps `source: 'jd'` so a frozen estimate can always say where its number came from.
//
// `ftfRows` are jd_field_ftf yield rows: acres + mean_value per CLU, carrying a county.
export function jdCountySuggestion(ftfRows = [], { crop = null, year = null } = {}) {
  const want = String(crop || "").trim().toUpperCase();
  const by = new Map();
  for (const r of ftfRows || []) {
    if (year != null && r.operation_date
        && Number(String(r.operation_date).slice(0, 4)) !== Number(year)) continue;
    if (want && String(r.crop || "").trim().toUpperCase() !== want) continue;
    const county = String(r.county || "").trim().toUpperCase();
    const ac = num(r.acres), mv = num(r.mean_value);
    // A grid with no yield contributes ACRES to nothing — a zero would read as a failed
    // field rather than as a pass we cannot value.
    if (!county || ac == null || ac <= 0 || mv == null || mv <= 0) continue;
    const cur = by.get(county) || { county, acres: 0, bushels: 0, grids: 0 };
    cur.acres += ac; cur.bushels += ac * mv; cur.grids++;
    by.set(county, cur);
  }
  return [...by.values()]
    .map((r) => ({ ...r, acres: +r.acres.toFixed(1), bushels: Math.round(r.bushels) }))
    .sort((a, b) => b.bushels - a.bushels);
}
